import {
	ProductInfo,
	CoverItem,
	PredefinedConfiguration,
	RangeSettings,
	MissingProductItem,
	SofaConfigurationItem,
} from 'sofa-reborn-react';

import { parseCoverItems } from '../../../util/SofaFamilyParser';
import { ProductUtils } from '../../../util/ProductUtils';
import ThunksUtil from '../utils/ThunksUtil';
import VpcThunk from '../thunk/VpcThunk';
import { RootState } from '../../../store';

import { ProductData } from '@ikea-aoa/component-view-3d';

export default class CoverParser {
	public async parse(
		productInfo: ProductInfo,
		products: ProductData,
		missingProducts: MissingProductItem[],
		missingArmrestCovers: string[],
		allPredefinedConfigurations: PredefinedConfiguration[],
		selectedConfiguration: string | null,
		state: RootState,
		rangeSettings: RangeSettings
	): Promise<{ currentCover: CoverItem; coverItems: CoverItem[] }> {
		let coverItems = parseCoverItems(productInfo, products, rangeSettings);
		coverItems = this.removeMissingColors(coverItems, missingArmrestCovers);
		if (rangeSettings.rangeName === 'VIMLE') {
			coverItems = this.removeCoversWithoutArmrests(coverItems, products);
		}

		// Get selected cover if reloaded or select first available cover in list
		const currentCover = await this.getCurrentCover(
			coverItems,
			state,
			selectedConfiguration,
			products,
			missingProducts,
			allPredefinedConfigurations,
			rangeSettings
		);
		return { currentCover, coverItems };
	}

	private async getCurrentCover(
		coverItems: CoverItem[],
		state: RootState,
		selectedConfiguration: string | null,
		products: ProductData,
		missingProducts: MissingProductItem[],
		allPredefinedConfigurations: PredefinedConfiguration[],
		rangeSettings: RangeSettings
	) {
		const vpcThunk = new VpcThunk();
		let currentCover = coverItems?.some(
			(cover) => cover.articleId === state.cover.selectedCover?.articleId
		)
			? state.cover.selectedCover
			: selectedConfiguration
			? selectedConfiguration.length > 6
				? this.getPredefinedOrAvailableCover(
						products,
						missingProducts,
						allPredefinedConfigurations,
						selectedConfiguration,
						coverItems,
						rangeSettings
				  )
				: await vpcThunk.getVpcOrAvailableCover(
						state.settings.settings,
						products,
						missingProducts,
						selectedConfiguration,
						coverItems,
						rangeSettings
				  )
			: coverItems && coverItems[0];

		// Cover not available, change to the first coverItem
		if (!currentCover) {
			currentCover = coverItems[0];
		}
		return currentCover;
	}

	private removeMissingColors(
		coverItems: CoverItem[],
		missingArmrestColors: string[]
	): CoverItem[] {
		let index = 0;
		missingArmrestColors.forEach(function (coverName) {
			index = coverItems.findIndex((coverItem) => coverItem.coverName === coverName);
			if (index > -1) {
				coverItems.splice(index, 1);
			}
		});

		return coverItems;
	}

	private getPredefinedOrAvailableCover(
		products: ProductData,
		missingProducts: MissingProductItem[],
		predefinedConfigurations: PredefinedConfiguration[],
		selectedConfiguration: string,
		coverItems: CoverItem[],
		rangeSettings: RangeSettings
	): CoverItem | null {
		const configuration = predefinedConfigurations.find(
			(predefinedConfiguration) => predefinedConfiguration.id === selectedConfiguration
		);

		if (
			configuration?.configuration.children &&
			configuration.configuration.children.length > 0 &&
			configuration.configuration.children[0].articleId
		) {
			const hasMissingAutoConnectProduct = this.hasMissingAutoConnectProduct(
				rangeSettings,
				products,
				missingProducts,
				configuration
			);

			const hasMissingProduct = this.hasMissingProduct(
				hasMissingAutoConnectProduct,
				products,
				missingProducts,
				configuration
			);

			if (hasMissingAutoConnectProduct || hasMissingProduct) {
				const configurationModelIds = ProductUtils.getModelIdsForConfiguration(
					products,
					missingProducts,
					configuration.configuration.children as SofaConfigurationItem[],
					rangeSettings.shouldColorAllModules as boolean,
					false
				);

				const availableCovers = ProductUtils.getAllAvailableCovers(
					products,
					coverItems,
					configurationModelIds
				);
				if (availableCovers.length > 0) {
					return availableCovers[0];
				}
			} else {
				const coverName = configuration.defaultColor as string;
				const coverItem = coverItems.find((coverItem) => {
					return coverItem.coverName === coverName;
				});
				if (coverItem) {
					return coverItem;
				}
			}
		}
		return null;
	}

	private hasMissingProduct(
		hasMissingAutoConnectProduct: boolean,
		products: ProductData,
		missingProducts: MissingProductItem[],
		configuration: PredefinedConfiguration
	) {
		let hasMissingProduct = false;
		if (!hasMissingAutoConnectProduct) {
			hasMissingProduct = ThunksUtil.containsMissingProduct(
				products,
				missingProducts,
				configuration.configuration.children
			);
		}
		return hasMissingProduct;
	}

	private hasMissingAutoConnectProduct(
		rangeSettings: RangeSettings,
		products: ProductData,
		missingProducts: MissingProductItem[],
		configuration: PredefinedConfiguration
	) {
		return rangeSettings.addAutomatically || rangeSettings.addArmrestsAutomatically
			? ThunksUtil.hasMissingAutoConnectProduct(
					rangeSettings,
					products,
					missingProducts,
					configuration.configuration.children
			  )
			: false;
	}

	private removeCoversWithoutArmrests(coverItems: CoverItem[], products: ProductData): CoverItem[] {
		return coverItems.filter((coverItem: CoverItem) => {
			const armrestsWithCover = Object.values(products).filter(
				(product) =>
					product.filter.color === coverItem.coverName && product.filter.family === 'ARMREST'
			);

			return armrestsWithCover.some((product) => !product.sprIdInvalid);
		});
	}
}
